import { useState, useEffect } from 'react';
import { useMoanaPaye } from '../../contexts/MoanaPayeContext';
import { Plus, Users, X, ChevronRight, Baby, FileText, AlertTriangle } from 'lucide-react';

const Field = ({ label, children, required }) => (
  <div>
    <label className="text-slate-400 text-xs font-medium mb-1 block">{label}{required && <span className="text-[#E8A020] ml-0.5">*</span>}</label>
    {children}
  </div>
);
const Input = (props) => <input {...props} className={`w-full bg-[#0f1520] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#2D5A5A] ${props.className || ''}`} />;
const Select = ({ children, ...props }) => <select {...props} className="w-full bg-[#0f1520] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#2D5A5A]">{children}</select>;

const MOTIFS_REPRISE = ['Mutation', 'Rachat', 'Accord CCT', 'Accord individuel'];

export default function SalariesPage() {
  const { mpFetch } = useMoanaPaye();
  const [entreprises, setEntreprises] = useState([]);
  const [selectedEnt, setSelectedEnt] = useState('');
  const [list, setList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [contratForm, setContratForm] = useState(null);
  const [enfantForm, setEnfantForm] = useState(null);

  useEffect(() => { mpFetch('/api/moana-paye/entreprises').then(d => { setEntreprises(d); if (d.length) setSelectedEnt(d[0].id); }).catch(console.error); }, []);
  useEffect(() => { if (selectedEnt) mpFetch(`/api/moana-paye/entreprises/${selectedEnt}/salaries`).then(setList).catch(console.error); }, [selectedEnt]);

  const openNew = () => {
    setForm({ entreprise_id: selectedEnt, nom: '', prenom: '', statut: 'MO', date_entree: '', date_anciennete: '', nationalite: 'Francaise' });
    setShowForm(true);
    setShowDetail(null);
  };

  const openDetail = async (sid) => {
    const data = await mpFetch(`/api/moana-paye/salaries/${sid}`);
    setShowDetail(data);
    setShowForm(false);
  };

  const saveSalarie = async () => {
    setSaving(true);
    try {
      const data = { ...form };
      if (!data.date_anciennete) data.date_anciennete = data.date_entree;
      await mpFetch('/api/moana-paye/salaries', { method: 'POST', body: JSON.stringify(data) });
      setShowForm(false);
      mpFetch(`/api/moana-paye/entreprises/${selectedEnt}/salaries`).then(setList);
    } catch (err) { alert(err.message); } finally { setSaving(false); }
  };

  const saveContrat = async () => {
    setSaving(true);
    try {
      const data = { ...contratForm, salarie_id: showDetail.id, entreprise_id: showDetail.entreprise_id };
      const res = await mpFetch('/api/moana-paye/contrats', { method: 'POST', body: JSON.stringify(data) });
      if (res.alertes?.length) alert(res.alertes.join('\n'));
      setContratForm(null);
      openDetail(showDetail.id);
    } catch (err) { alert(err.message); } finally { setSaving(false); }
  };

  const saveEnfant = async () => {
    setSaving(true);
    try {
      await mpFetch(`/api/moana-paye/salaries/${showDetail.id}/enfants`, { method: 'POST', body: JSON.stringify(enfantForm) });
      setEnfantForm(null);
      openDetail(showDetail.id);
    } catch (err) { alert(err.message); } finally { setSaving(false); }
  };

  const deleteEnfant = async (eid) => {
    if (!window.confirm('Supprimer cet enfant ?')) return;
    await mpFetch(`/api/moana-paye/salaries/${showDetail.id}/enfants/${eid}`, { method: 'DELETE' });
    openDetail(showDetail.id);
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const diffDates = form.date_anciennete && form.date_entree && form.date_anciennete !== form.date_entree;

  // ===== DETAIL VIEW =====
  if (showDetail) {
    const s = showDetail;
    return (
      <div data-testid="mp-salarie-detail">
        <div className="flex items-center justify-between mb-6">
          <div>
            <button onClick={() => setShowDetail(null)} className="text-slate-500 text-xs hover:text-white mb-1">&larr; Retour</button>
            <h2 className="text-xl font-bold text-white">{s.prenom} {s.nom}</h2>
            <p className="text-slate-400 text-sm">{s.poste_occupe} &middot; {s.statut} &middot; Anciennete: {s.anciennete_annees} ans {s.anciennete_mois} mois</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          {/* Infos */}
          <div className="bg-[#1a2332] border border-white/10 rounded-xl p-5">
            <h3 className="text-[#E8A020] text-xs font-semibold tracking-wider uppercase mb-3">Identite</h3>
            <div className="grid grid-cols-2 gap-y-2 text-sm">
              <span className="text-slate-500">N CPS</span><span className="text-white">{s.numero_cps || '-'}</span>
              <span className="text-slate-500">Date entree</span><span className="text-white">{s.date_entree}</span>
              <span className="text-slate-500">Date anciennete</span><span className="text-white">{s.date_anciennete}{s.motif_reprise_anciennete ? ` (${s.motif_reprise_anciennete})` : ''}</span>
              <span className="text-slate-500">Email</span><span className="text-white">{s.email || '-'}</span>
              <span className="text-slate-500">Telephone</span><span className="text-white">{s.telephone || '-'}</span>
              <span className="text-slate-500">IBAN</span><span className="text-white font-mono text-xs">{s.iban || '-'}</span>
            </div>
          </div>

          {/* Contrat actif */}
          <div className="bg-[#1a2332] border border-white/10 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[#E8A020] text-xs font-semibold tracking-wider uppercase">Contrat actif</h3>
              <button onClick={() => setContratForm({ type_contrat: 'CDI', date_debut: '', salaire_base: '', temps_travail: 'plein', heures_hebdo: 39 })} className="text-[#2D5A5A] hover:text-[#E8A020] text-xs flex items-center gap-1"><Plus className="w-3 h-3" />Nouveau</button>
            </div>
            {s.contrat ? (
              <div className="grid grid-cols-2 gap-y-2 text-sm">
                <span className="text-slate-500">Type</span><span className="text-white">{s.contrat.type_contrat}{s.contrat.type_contrat === 'CDD' ? ` (n${s.contrat.numero_cdd}/2)` : ''}</span>
                <span className="text-slate-500">Salaire base</span><span className="text-white font-medium">{Number(s.contrat.salaire_base).toLocaleString('fr-FR')} F CFP</span>
                <span className="text-slate-500">Horaire</span><span className="text-white">{s.contrat.heures_mensuelles}h/mois ({s.contrat.temps_travail})</span>
                <span className="text-slate-500">Debut</span><span className="text-white">{s.contrat.date_debut}</span>
                {s.contrat.date_fin && <><span className="text-slate-500">Fin</span><span className="text-white">{s.contrat.date_fin}</span></>}
              </div>
            ) : <p className="text-slate-500 text-sm">Aucun contrat actif</p>}
          </div>
        </div>

        {/* Enfants */}
        <div className="bg-[#1a2332] border border-white/10 rounded-xl p-5 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[#E8A020] text-xs font-semibold tracking-wider uppercase">Enfants a charge</h3>
            <button onClick={() => setEnfantForm({ prenom: '', date_naissance: '' })} className="text-[#2D5A5A] hover:text-[#E8A020] text-xs flex items-center gap-1"><Plus className="w-3 h-3" />Ajouter</button>
          </div>
          {s.enfants?.length > 0 ? (
            <div className="space-y-2">
              {s.enfants.map(e => (
                <div key={e.id} className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-3">
                    <Baby className="w-4 h-4 text-slate-500" />
                    <span className="text-white text-sm">{e.prenom}</span>
                    <span className="text-slate-500 text-xs">{e.age} ans</span>
                    {e.alerte_16ans && <span className="text-xs bg-orange-500/15 text-orange-400 px-2 py-0.5 rounded-full flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Bientot 16 ans</span>}
                    <span className={`text-xs px-2 py-0.5 rounded-full ${e.a_charge ? 'bg-green-500/15 text-green-400' : 'bg-red-500/15 text-red-400'}`}>{e.a_charge ? 'A charge' : 'Plus a charge'}</span>
                  </div>
                  <button onClick={() => deleteEnfant(e.id)} className="text-slate-600 hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          ) : <p className="text-slate-500 text-sm">Aucun enfant enregistre</p>}
        </div>

        {/* Modal contrat */}
        {contratForm && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setContratForm(null)}>
            <div className="bg-[#1a2332] border border-white/10 rounded-xl p-5 w-full max-w-lg" onClick={e => e.stopPropagation()}>
              <h3 className="text-white font-semibold mb-4">Nouveau contrat</h3>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <Field label="Type" required><Select value={contratForm.type_contrat} onChange={e => setContratForm(f => ({ ...f, type_contrat: e.target.value }))}><option value="CDI">CDI</option><option value="CDD">CDD</option></Select></Field>
                <Field label="Date début" required><Input type="date" value={contratForm.date_debut} onChange={e => setContratForm(f => ({ ...f, date_debut: e.target.value }))} /></Field>
                {contratForm.type_contrat === 'CDD' && <>
                  <Field label="Date fin"><Input type="date" value={contratForm.date_fin || ''} onChange={e => setContratForm(f => ({ ...f, date_fin: e.target.value }))} /></Field>
                  <Field label="Motif CDD"><Input value={contratForm.motif_cdd || ''} onChange={e => setContratForm(f => ({ ...f, motif_cdd: e.target.value }))} /></Field>
                </>}
                <Field label="Salaire base (F CFP)" required><Input type="number" value={contratForm.salaire_base} onChange={e => setContratForm(f => ({ ...f, salaire_base: parseFloat(e.target.value) }))} /></Field>
                <Field label="Temps"><Select value={contratForm.temps_travail} onChange={e => setContratForm(f => ({ ...f, temps_travail: e.target.value }))}><option value="plein">Temps plein (39h)</option><option value="partiel">Temps partiel</option></Select></Field>
                {contratForm.temps_travail === 'partiel' && <Field label="Heures/semaine"><Input type="number" value={contratForm.heures_hebdo} onChange={e => setContratForm(f => ({ ...f, heures_hebdo: parseFloat(e.target.value) }))} /></Field>}
              </div>
              <div className="flex justify-end gap-3">
                <button onClick={() => setContratForm(null)} className="px-4 py-2 text-sm text-slate-400">Annuler</button>
                <button onClick={saveContrat} disabled={saving} className="px-4 py-2 bg-[#2D5A5A] text-white text-sm rounded-lg disabled:opacity-50">{saving ? 'Enregistrement...' : 'Créer'}</button>
              </div>
            </div>
          </div>
        )}

        {/* Modal enfant */}
        {enfantForm && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setEnfantForm(null)}>
            <div className="bg-[#1a2332] border border-white/10 rounded-xl p-5 w-full max-w-sm" onClick={e => e.stopPropagation()}>
              <h3 className="text-white font-semibold mb-4">Ajouter un enfant</h3>
              <div className="space-y-3 mb-4">
                <Field label="Prénom" required><Input value={enfantForm.prenom} onChange={e => setEnfantForm(f => ({ ...f, prenom: e.target.value }))} /></Field>
                <Field label="Date de naissance" required><Input type="date" value={enfantForm.date_naissance} onChange={e => setEnfantForm(f => ({ ...f, date_naissance: e.target.value }))} /></Field>
              </div>
              <div className="flex justify-end gap-3">
                <button onClick={() => setEnfantForm(null)} className="px-4 py-2 text-sm text-slate-400">Annuler</button>
                <button onClick={saveEnfant} disabled={saving} className="px-4 py-2 bg-[#2D5A5A] text-white text-sm rounded-lg disabled:opacity-50">{saving ? '...' : 'Ajouter'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ===== FORM VIEW =====
  if (showForm) return (
    <div data-testid="mp-salarie-form">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">Nouveau salarie</h2>
        <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
      </div>
      <div className="bg-[#1a2332] border border-white/10 rounded-xl p-5 space-y-6">
        <h3 className="text-[#E8A020] text-xs font-semibold tracking-wider uppercase">Identite</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field label="Nom" required><Input value={form.nom} onChange={e => set('nom', e.target.value)} data-testid="mp-sal-nom" /></Field>
          <Field label="Prenom" required><Input value={form.prenom} onChange={e => set('prenom', e.target.value)} data-testid="mp-sal-prenom" /></Field>
          <Field label="Date naissance"><Input type="date" value={form.date_naissance || ''} onChange={e => set('date_naissance', e.target.value)} /></Field>
          <Field label="Nationalite"><Input value={form.nationalite || ''} onChange={e => set('nationalite', e.target.value)} /></Field>
          <Field label="N CPS"><Input value={form.numero_cps || ''} onChange={e => set('numero_cps', e.target.value)} /></Field>
          <Field label="N AVS"><Input value={form.numero_avs || ''} onChange={e => set('numero_avs', e.target.value)} /></Field>
        </div>

        <h3 className="text-[#E8A020] text-xs font-semibold tracking-wider uppercase pt-2">Poste & Statut</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field label="Poste occupe"><Input value={form.poste_occupe || ''} onChange={e => set('poste_occupe', e.target.value)} /></Field>
          <Field label="Statut"><Select value={form.statut} onChange={e => set('statut', e.target.value)}><option value="MO">MO</option><option value="ETAM">ETAM</option><option value="Cadre">Cadre</option></Select></Field>
          <Field label="CCT applicable"><Input value={form.cct_applicable || ''} onChange={e => set('cct_applicable', e.target.value)} /></Field>
        </div>

        <h3 className="text-[#E8A020] text-xs font-semibold tracking-wider uppercase pt-2">Dates</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field label="Date d'entree" required><Input type="date" value={form.date_entree} onChange={e => { set('date_entree', e.target.value); if (!form.date_anciennete) set('date_anciennete', e.target.value); }} data-testid="mp-sal-entree" /></Field>
          <Field label="Date d'anciennete" required><Input type="date" value={form.date_anciennete || form.date_entree || ''} onChange={e => set('date_anciennete', e.target.value)} /></Field>
          {diffDates && (
            <Field label="Motif reprise anciennete" required>
              <Select value={form.motif_reprise_anciennete || ''} onChange={e => set('motif_reprise_anciennete', e.target.value)}>
                <option value="">-- Selectionner --</option>
                {MOTIFS_REPRISE.map(m => <option key={m} value={m}>{m}</option>)}
              </Select>
            </Field>
          )}
        </div>
        {diffDates && <div className="bg-[#E8A020]/10 border border-[#E8A020]/30 text-[#E8A020] text-xs px-3 py-2 rounded-lg">Les dates d'entree et d'anciennete sont differentes. Le motif de reprise est obligatoire.</div>}

        <h3 className="text-[#E8A020] text-xs font-semibold tracking-wider uppercase pt-2">Coordonnees & Banque</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field label="Adresse"><Input value={form.adresse_ligne1 || ''} onChange={e => set('adresse_ligne1', e.target.value)} /></Field>
          <Field label="Ville"><Input value={form.ville || ''} onChange={e => set('ville', e.target.value)} /></Field>
          <Field label="Téléphone"><Input value={form.telephone || ''} onChange={e => set('telephone', e.target.value)} /></Field>
          <Field label="Email"><Input type="email" value={form.email || ''} onChange={e => set('email', e.target.value)} /></Field>
          <Field label="IBAN"><Input value={form.iban || ''} onChange={e => set('iban', e.target.value)} /></Field>
          <Field label="Banque"><Input value={form.banque || ''} onChange={e => set('banque', e.target.value)} /></Field>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
          <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-slate-400">Annuler</button>
          <button onClick={saveSalarie} disabled={saving || !form.nom || !form.prenom || !form.date_entree} className="px-5 py-2 bg-[#2D5A5A] text-white text-sm font-medium rounded-lg disabled:opacity-50" data-testid="mp-sal-save">
            {saving ? 'Enregistrement...' : 'Créer le salarié'}
          </button>
        </div>
      </div>
    </div>
  );

  // ===== LIST VIEW =====
  return (
    <div data-testid="mp-salaries">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">Salaries</h2>
        <button onClick={openNew} disabled={!selectedEnt} className="flex items-center gap-2 bg-[#2D5A5A] hover:bg-[#3a7272] text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50" data-testid="mp-sal-new">
          <Plus className="w-4 h-4" /> Nouveau salarie
        </button>
      </div>

      {entreprises.length > 1 && (
        <div className="mb-4">
          <select value={selectedEnt} onChange={e => setSelectedEnt(e.target.value)} className="bg-[#1a2332] border border-white/10 rounded-lg px-3 py-2 text-white text-sm">
            {entreprises.map(e => <option key={e.id} value={e.id}>{e.raison_sociale}</option>)}
          </select>
        </div>
      )}

      {list.length === 0 ? (
        <div className="bg-[#1a2332] border border-white/10 rounded-xl p-10 text-center">
          <Users className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">Aucun salarie</p>
        </div>
      ) : (
        <div className="space-y-2">
          {list.map(sal => (
            <div key={sal.id} className="bg-[#1a2332] border border-white/10 rounded-xl p-4 flex items-center justify-between hover:border-[#2D5A5A]/50 transition-colors cursor-pointer" onClick={() => openDetail(sal.id)}>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-[#2D5A5A]/20 flex items-center justify-center text-[#2D5A5A] font-bold text-sm">{sal.prenom?.[0]}{sal.nom?.[0]}</div>
                <div>
                  <div className="text-white font-medium text-sm">{sal.prenom} {sal.nom}</div>
                  <div className="text-slate-500 text-xs">{sal.poste_occupe || '-'} &middot; {sal.statut} &middot; {sal.type_contrat || '-'} &middot; {sal.salaire_base ? Number(sal.salaire_base).toLocaleString('fr-FR') + ' F CFP' : '-'}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {sal.nb_enfants > 0 && <span className="text-xs text-slate-500 flex items-center gap-1"><Baby className="w-3 h-3" />{sal.nb_enfants}</span>}
                <ChevronRight className="w-4 h-4 text-slate-600" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
